import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes
} from 'react';

import { Icon } from './Icon';

/**
 * The portal's small form and feedback primitives: a labelled field wrapper,
 * text and numeric inputs, a select, a checkbox, a slide toggle, a spinner and
 * an expansion panel.
 *
 * Each renders the flat, bordered control the design calls for directly - a
 * label, the control, and a hint or error line - with no wrapper scaffold in
 * between. Layout for the `cw-*` hooks lives in `styles/_ui-primitives.scss`.
 */

/* ------------------------------------------------------------------ field ---- */

export interface FormFieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  htmlFor?: string;
  /**
   * Label casing. `upper` is the portal's established treatment and stays the
   * default; `sentence` is for dense forms, where forty tracked uppercase
   * labels read as forty shouts and nothing stands out. Opt-in, so no existing
   * screen changes.
   */
  labelCase?: 'upper' | 'sentence';
  children: ReactNode;
}

/** The label + control + hint stack shared by every control below. */
export function FormField({
  label,
  hint,
  error,
  required,
  className,
  htmlFor,
  labelCase = 'upper',
  children
}: FormFieldProps) {
  return (
    <div className={['cw-field', error ? 'cw-field--invalid' : null, className].filter(Boolean).join(' ')}>
      {label && (
        <label
          className={['cw-field__label', labelCase === 'sentence' ? 'cw-field__label--sentence' : null]
            .filter(Boolean)
            .join(' ')}
          htmlFor={htmlFor}
        >
          {label}
          {required && <span className="cw-field__required" aria-hidden="true"> *</span>}
        </label>
      )}

      {children}

      {error ? (
        <span className="cw-field__error" role="alert">{error}</span>
      ) : (
        hint && <span className="cw-field__hint">{hint}</span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ input ---- */

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={['form-control', 'cw-input', className].filter(Boolean).join(' ')}
        {...rest}
      />
    );
  }
);

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function TextArea({ className, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        className={['form-control', 'cw-input', className].filter(Boolean).join(' ')}
        {...rest}
      />
    );
  }
);

/* ----------------------------------------------------------------- select ---- */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: SelectOption[];
  /** Renders a leading empty option for the unset state. */
  placeholder?: string;
}

/**
 * A native `<select>` rather than a rebuilt overlay listbox.
 *
 * The select appears seven times in the app and always as a plain single
 * choice - no multi-select, no option templates, no custom trigger. A native
 * control gets keyboard behaviour, type-ahead and the platform's own mobile
 * picker for free, which is strictly better than a hand-rolled listbox here.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, className, ...rest },
  ref
) {
  return (
    <select
      ref={ref}
      className={['form-control', 'cw-select', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
});

/* --------------------------------------------------------------- checkbox ---- */

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
}

export function Checkbox({ label, className, id, ...rest }: CheckboxProps) {
  const generated = useId();
  const inputId = id ?? generated;

  return (
    <span className={['cw-checkbox', className].filter(Boolean).join(' ')}>
      <input id={inputId} type="checkbox" {...rest} />
      {label && <label htmlFor={inputId}>{label}</label>}
    </span>
  );
}

/* ----------------------------------------------------------- slide toggle ---- */

export interface SlideToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

/** A checkbox visually reskinned as a switch. */
export function SlideToggle({ checked, onChange, label, disabled, className, ariaLabel }: SlideToggleProps) {
  return (
    <label className={['cw-slide-toggle', className].filter(Boolean).join(' ')}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.checked)}
        /* Visually hidden, but still the real focusable, checkable control. */
        style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' }}
      />
      <span className="cw-slide-toggle__track" aria-hidden="true">
        <span className="cw-slide-toggle__thumb" />
      </span>
      {label && <span className="cw-slide-toggle__label">{label}</span>}
    </label>
  );
}

/* ---------------------------------------------------------------- spinner ---- */

/**
 * 14px, and only ever inside the control that started the wait - a button, a
 * row, a toggle, a search field. The content area waits with a skeleton
 * (`<Loading>`), never with this.
 */
export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <span
      className={['cw-spinner', className].filter(Boolean).join(' ')}
      role="progressbar"
      aria-label={label}
    />
  );
}

/* -------------------------------------------------------------- expansion ---- */

export interface ExpansionPanelProps {
  title: ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean;
  className?: string;
}

export function ExpansionPanel({ title, children, defaultExpanded = false, className }: ExpansionPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const id = useId();

  return (
    <div className={['cw-panel', className].filter(Boolean).join(' ')}>
      <button
        type="button"
        className="cw-panel__header"
        aria-expanded={expanded}
        aria-controls={id}
        onClick={() => setExpanded((value) => !value)}
      >
        <span>{title}</span>
        <Icon>{expanded ? 'expand_less' : 'expand_more'}</Icon>
      </button>

      {expanded && (
        <div className="cw-panel__body" id={id}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ radio group ---- */

export interface RadioOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  options: RadioOption[];
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  /** Stacks the options instead of laying them out in a row. */
  vertical?: boolean;
}

/**
 * A single choice made from two or three visible options.
 *
 * A `<Select>` hides its options behind a click, which is the wrong trade when
 * there are only a couple and the choice changes what the rest of the form
 * asks for - Written vs Verbal being exactly that case.
 *
 * Rendered as real radios in a `role="radiogroup"`, so arrow keys move between
 * them and the group reads out as one control.
 */
export function RadioGroup({
  name,
  options,
  value,
  onChange,
  disabled,
  className,
  vertical = false
}: RadioGroupProps) {
  return (
    <div
      role="radiogroup"
      className={['cw-radio-group', vertical ? 'cw-radio-group--vertical' : null, className]
        .filter(Boolean)
        .join(' ')}
    >
      {options.map((option) => (
        <label className="cw-radio" key={option.value}>
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            disabled={disabled || option.disabled}
            onChange={() => onChange(option.value)}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- date input ---- */

export interface DateInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  /** ISO `yyyy-MM-dd`, or null for unset. */
  value: string | null;
  onValueChange: (value: string | null) => void;
}

/**
 * A date, held as ISO `yyyy-MM-dd` and displayed in the viewer's locale.
 *
 * The native picker rather than a rebuilt calendar: it is keyboard-operable,
 * it is the control the platform's assistive tech already knows, and on a
 * phone it opens the system date wheel. It also renders dd/MM/yyyy for a
 * Singapore locale without the app having to format anything, which is what
 * the SG screens ask for.
 */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { value, onValueChange, className, ...rest },
  ref
) {
  return (
    <input
      ref={ref}
      type="date"
      className={['form-control', 'cw-input', className].filter(Boolean).join(' ')}
      value={value ?? ''}
      onChange={(event) => onValueChange(event.target.value ? event.target.value : null)}
      {...rest}
    />
  );
});

/* ----------------------------------------------------------- number input ---- */

export type NumberMode = 'decimal' | 'money' | 'integer';

export interface NumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'prefix'> {
  value: number | null;
  onValueChange: (value: number | null) => void;
  mode?: NumberMode;
  /** Decimal places used when the value is displayed, not while it is typed. */
  decimals?: number;
  /** A leading unit or currency, e.g. `S$`. */
  prefix?: ReactNode;
  /**
   * BCP 47 tag the value is grouped in when it is not being edited. Editing
   * always shows the raw number, so this changes what is read, never what is
   * parsed.
   */
  locale?: string;
}

function formatNumber(value: number, mode: NumberMode, decimals: number, locale: string): string {
  if (mode === 'integer') {
    return String(Math.round(value));
  }

  return value.toLocaleString(locale, {
    minimumFractionDigits: mode === 'money' ? decimals : 0,
    maximumFractionDigits: decimals
  });
}

/**
 * A number that reads as a number.
 *
 * Grouped thousands are the point: `12500000` and `1250000` are one glance
 * apart in a valuation range and the mistake is expensive. Formatting is
 * applied on blur and dropped again on focus, so what is being typed is always
 * the raw value - a separator appearing mid-keystroke is what makes most
 * formatted inputs fight the person using them.
 *
 * `type="text"` with a numeric inputmode rather than `type="number"`: the
 * number type refuses grouped text, and its scroll-wheel stepping silently
 * changes values people are only scrolling past.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { value, onValueChange, mode = 'decimal', decimals = 2, prefix, locale = 'en', className, onFocus, onBlur, ...rest },
  ref
) {
  const [draft, setDraft] = useState<string | null>(null);

  const display = draft ?? (value === null || value === undefined ? '' : formatNumber(value, mode, decimals, locale));

  const input = (
    <input
      ref={ref}
      type="text"
      inputMode={mode === 'integer' ? 'numeric' : 'decimal'}
      className={['form-control', 'cw-input', 'cw-number', className].filter(Boolean).join(' ')}
      value={display}
      onFocus={(event) => {
        setDraft(value === null || value === undefined ? '' : String(value));
        onFocus?.(event);
      }}
      onChange={(event) => {
        const next = event.target.value;
        setDraft(next);

        const cleaned = next.replace(/,/g, '').trim();

        if (cleaned === '' || cleaned === '-') {
          onValueChange(null);
          return;
        }

        const parsed = Number(cleaned);

        // A half-typed number ("1.") parses as NaN; hold the last good value
        // rather than clearing the field out from under the keystroke.
        if (!Number.isNaN(parsed)) {
          onValueChange(mode === 'integer' ? Math.round(parsed) : parsed);
        }
      }}
      onBlur={(event) => {
        setDraft(null);
        onBlur?.(event);
      }}
      {...rest}
    />
  );

  if (!prefix) {
    return input;
  }

  return (
    <span className="cw-number-wrap">
      <span className="cw-number-wrap__prefix" aria-hidden="true">
        {prefix}
      </span>
      {input}
    </span>
  );
});
